import React, { useEffect } from "react";
import { Viewer, Worker } from "@react-pdf-viewer/core";
import "@react-pdf-viewer/core/lib/styles/index.css";
import { Box, Button, Typography } from "@mui/material";
import { useState } from "react";
import SRSIconButton from "../buttons/SRSIconButton";
import validateDocument from "../api/folder/validateDocument";
import { getOrRefreshFirebaseToken } from "../config/getFirebaseToken";
import refuseFolder from "../api/folder/refuseFolder";
import {
  getDocDataById,
  getFlutterTime,
  TruncatedString,
  updateDocDataField,
} from "../models/DataFunctions";
import PrimaryButton from "../buttons/PrimaryButton";
import SecondaryButton from "../buttons/SecondaryButton";
import { useAppContext } from "../config/AppContext";
import downloadMultiPDF from "../api/download/downloadMultiPDF";
import SecondaryButtonIcon from "../buttons/SecondaryButtonIcon";
import Icon1 from "../icons/Icon";
import SmallSpinner from "../loading/SmallSpinner";
import Space from "../layout/Space";
import Modal1 from "../modals/Modal1";
import WarningButton from "../buttons/WarningButton";
import { useSnackbar } from "notistack";
import updateDocDataFieldApi from "../api/database/updateDocDataFieldApi";
import { useAuth } from "../auth/FirebaseAuth";
import createDocApi from "../api/database/createDocApi";
import { translateDocumentId } from "../pages/folder_pages/FolderV2";
import { addActivityLog } from "../config/OnlineStatus";
import Hexasign from "../sign/new_sign/Hexasign";
import { isFolderAdmin } from "../pages/folder_pages/FolderHelpers";
import { isFolderClient } from "../hexagone/VisibilityHelpers";
// Worker URL should match the version of the pdfjs-dist package
const workerUrl =
  "https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js";

const PDFVisualisateur = ({
  document,
  folder,
  type,
  files = [],
  expectedDocuments = ["Etude thermique", "Synthèse", "Dossier VMC"],
  onClose,
  orgData,
  handleDeleteFile,
  onUpdate,
  userData,
}) => {
  const [selectedPdf, setSelectedPdf] = useState(
    files.length > 0 ? files[0] : null
  );
  const { enqueueSnackbar } = useSnackbar();
  const handleClickVariant = (message, variant) => {
    enqueueSnackbar(message, { variant });
  };

  useEffect(() => {
    setSelectedPdf(files.length > 0 ? files[0] : null);
  }, [files]);

  const { appState, setAppState } = useAppContext();
  const prefix = appState.dev ? appState.dev_url : appState.prod_url;

  const downloadPdf = (pdfUrl) => {
    const link = document.createElement("a");
    link.href = pdfUrl;
    link.download = "document.pdf";
    link.click();
  };

  const handleValidate = async () => {
    setLoadingData(true);
    if (type !== "rapport-payment" && type !== "rapport-slot") {
      const token = await getOrRefreshFirebaseToken();
      await validateDocument(document.id, folder.id, type, token, prefix);

      let repo_name = await translateDocumentId(type);

      await addActivityLog(
        "Validation de dépôt de " + repo_name,
        folder.ref,
        orgData.id,
        userData.id,
        "document_validated",
        prefix
      );
    }
    handleClickVariant("Document validé avec succès", "success");

    onClose();
    setLoadingData(false);
  };

  const handleRefus = async () => {
    setOpenRefus(false);
    const token = await getOrRefreshFirebaseToken();
    await refuseFolder(folder.id, document.id, token, type, prefix);
    let repo_name = await translateDocumentId(type);

    await addActivityLog(
      "Refus de dépôt de " + repo_name,
      folder.ref,
      orgData.id,
      userData.id,
      "document_refused",
      prefix
    );
    handleClickVariant("Document refusé avec succès", "success");

    onClose();
  };

  function getPieceName() {
    if (type === "thermal") {
      return "Documents de l'étude thermique";
    } else if (type === "modif_thermal") {
      return "Documents de la modification de l'étude thermique";
    } else if (type === "doc") {
      return document.name;
    } else if (type === "rapport") {
      return "Rapports de fin de chantier";
    } else if (type === "rapport-slot") {
      return "Rapports de fin de chantier";
    } else if (type === "rapport-payment") {
      return "Rapports de fin de chantier";
    } else if (type === "rapport_architecte") {
      return "Rapports de l'architecte";
    }

    return "Documents";
  }

  const handleDeleteFileLaunch = async (file) => {
    if (files.length === 1) {
      if (type === "doc") {
        await updateDocDataFieldApi(document.ref, "state", "locked", prefix);
      }
    }
    await handleDeleteFile(file);
    await onUpdate();
    handleClickVariant("Fichier supprimé avec succès", "success");
  };

  const [pdf_loading, setPdfLoading] = useState(false);

  const handleChangePDF = (file) => {
    setPdfLoading(file);
    setSelectedPdf(file);
  };

  const handleDownload = async () => {
    let name = getPieceName().replace(/ /g, "_");

    if (
      isFolderClient(orgData, folder) &&
      (type === "rapport-payment" || type === "rapport-slot")
    ) {
      if (type === "rapport-payment") {
        let payment_id = document.id.split("-")[2];
        let paymentData = await getDocDataById("payment", payment_id);
        if (!paymentData.downloaded) {
          await updateDocDataFieldApi(
            paymentData.ref,
            "downloaded",
            true,
            prefix
          );
        }
      } else if (type === "rapport-slot") {
        let slot_id = document.id.split("-")[2];
        let slotData = await getDocDataById("rapport_slot", slot_id);
        if (!slotData.downloaded) {
          await updateDocDataFieldApi(slotData.ref, "downloaded", true, prefix);
        }
      }
    }

    await downloadMultiPDF(files, name, folder.nom);
  };

  function isPDF(file) {
    return file.includes(".pdf") || file.includes(".PDF");
  }

  function isImage(file) {
    return (
      file.includes(".jpeg") || file.includes(".jpg") || file.includes(".png")
    );
  }

  function onCloseEdit() {
    onUpdate();
    setOpenEdit(false);
  }

  const [loadingData, setLoadingData] = useState(false);
  const [openRefus, setOpenRefus] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  // Refresh the PDF URLs

  return (
    <div className="flex flex-row justify-between rounded-xl gap-5 h-full w-full max-w-[90vw] max-h-[90vh] bg-white p-4  ">
      {openEdit ? (
        <Hexasign
          file_url={selectedPdf.url}
          folder={folder}
          onClose={onCloseEdit}
          mode="edit"
          fileName={selectedPdf.name}
          rapportType={type}
        />
      ) : (
        <>
          <Modal1 isOpen={openRefus} onClose={() => setOpenRefus(false)}>
            <div className=" flex flex-col w-c400 bg-white rounded-xl p-4">
              <div className="text-h2">
                Voulez-vous vraiment effectuer cette action ?
              </div>
              <div className="text-h3 text-shadow_50 my-4">
                Cette action est irréversible. Le refus entraîne la suppression
                définitive des fichiers déposés.
              </div>

              <div className="flex flex-row justify-end gap-2 ">
                <SecondaryButton onClick={() => setOpenRefus(false)}>
                  Annuler
                </SecondaryButton>
                <WarningButton onClick={handleRefus}>Confirmer</WarningButton>
              </div>
            </div>
          </Modal1>
          {/* Actions Sidebar */}
          <div className="flex flex-col gap-2 w-c300">
            {/* Quick Actions */}
            <SecondaryButton
              onClick={(e) => {
                e.stopPropagation();
                onClose(false);
              }}
              size={18}
              className="w-fit"
            >
              Fermer
            </SecondaryButton>
            <div className="text-h2 flex flex-col gap-1">
              {type === "rapport-payment" || type === "rapport-slot" ? (
                <PrimaryButton
                  onClick={handleDownload}
                  size={18}
                  className="w-fit"
                >
                  Télécharger
                </PrimaryButton>
              ) : (
                <SecondaryButton
                  onClick={handleDownload}
                  size={18}
                  className="w-fit"
                >
                  Télécharger
                </SecondaryButton>
              )}
            </div>

            {/* Expected Documents */}
            {type === "thermal" && (
              <div className="text-h2 flex flex-col gap-1">
                {expectedDocuments.length > 0 ? (
                  expectedDocuments.map((doc, index) => (
                    <div key={index}>- {doc}</div>
                  ))
                ) : (
                  <div>Aucune pièce attendue</div>
                )}
              </div>
            )}

            {/* Validation Section */}
            {isFolderAdmin(orgData, folder) && (
              <div>
                <div className="text-h2 flex flex-col gap-1">
                  <div className="flex flex-col gap-2 w-fit">
                    {type.includes("rapport") && (
                      <SecondaryButton
                        onClick={() => {
                          setOpenEdit(true);
                        }}
                        size={18}
                        className="w-fit"
                      >
                        Modifier le PDF
                      </SecondaryButton>
                    )}
                    {type === "rapport-payment" || type === "rapport-slot" ? (
                      <></>
                    ) : (
                      <>
                        <SecondaryButton
                          onClick={() => setOpenRefus(true)}
                          size={18}
                          className="w-fit"
                        >
                          Refuser le document
                        </SecondaryButton>
                        <PrimaryButton
                          onClick={handleValidate}
                          icon="done"
                          disabled={loadingData}
                        >
                          <div className="flex flex-row content-center align-middle items-center w-fit">
                            Valider le document <Space x={5} />
                            <SmallSpinner
                              loading={loadingData}
                              margin={0}
                              size={20}
                            />
                          </div>
                        </PrimaryButton>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
          <div className="flex flex-col w-full h-full">
            <div className="flex flex-wrap h-fit gap-1 items-center">
              {files.map((file, index) =>
                selectedPdf?.url === file.url ? (
                  <div
                    key={file.url + index}
                    className="flex border-2 gap-2 border-primary rounded-xl flex-row items-center pr-2"
                  >
                    <SecondaryButton
                      key={index}
                      variant="contained"
                      className="w-fit"
                      onClick={() => handleChangePDF(file)}
                    >
                      <SmallSpinner loading={pdf_loading === file} />
                      {isPDF(file.url)
                        ? "PDF"
                        : isImage(file.url)
                          ? "IMG"
                          : "Fichier"}{" "}
                      {TruncatedString(file.name, 20)}
                    </SecondaryButton>{" "}
                    {isFolderAdmin(orgData, folder) && (
                      <div
                        className="hover:bg-red-400 rounded-md cursor-pointer"
                        onClick={() => handleDeleteFileLaunch(file)}
                      >
                        <Icon1
                          icon_name="delete"
                          height="20px"
                          width="20px"
                          fill="grey"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <SecondaryButton
                    key={index}
                    variant="contained"
                    onClick={() => handleChangePDF(file)}
                  >
                    PDF {TruncatedString(file.name, 20)}
                  </SecondaryButton>
                )
              )}
            </div>

            {/* Full PDF Viewer Section */}
            <div className="flex flex-col h-full overflow-auto " flex={2} p={2}>
              {(selectedPdf == null || !selectedPdf?.url == null) && (
                <Typography variant="body1">
                  Veuillez sélectionner un PDF
                </Typography>
              )}
              {selectedPdf && selectedPdf.url && isPDF(selectedPdf.url) && (
                <>
                  <Worker workerUrl={workerUrl}>
                    <div className="flex flex-col gap-2 overflow-auto">
                      {/* Render the selected PDF */}
                      <Viewer
                        onDocumentLoad={() => setPdfLoading(null)}
                        fileUrl={selectedPdf.url}
                        defaultScale={2}
                      />
                    </div>
                  </Worker>
                  <iframe
                    style={{ display: "none" }}
                    src={selectedPdf.url}
                    title="hiddenFrame"
                    onLoad={() => {
                      setPdfLoading(null);
                    }}
                  ></iframe>
                </>
              )}

              {selectedPdf && selectedPdf.url && isImage(selectedPdf.url) && (
                <div
                  className="flex flex-col gap-2 overflow-auto"
                  style={{
                    height: "calc(100vh - 300px)",
                  }}
                >
                  <img
                    src={selectedPdf.url}
                    className=" max-h-full w-full object-contain rounded-xl"
                  ></img>
                </div>
              )}
              {selectedPdf &&
                selectedPdf.url &&
                !isPDF(selectedPdf.url) &&
                !isImage(selectedPdf.url) && (
                  <div
                    className="flex flex-col gap-2 overflow-auto"
                    style={{
                      height: "calc(100vh - 300px)",
                    }}
                  >
                    <div className="text-h1 text-shadow_50 h-full w-full items-center justify-center flex flex-col gap-2">
                      <Icon1
                        icon_name="warning"
                        height="30px"
                        width="30px"
                        fill="grey"
                      />
                      Ce document n'est pas visualisable (Fichier .zip, .xml,
                      .docx, .jpeg, .png, etc.)
                    </div>
                  </div>
                )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default PDFVisualisateur;
